import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ProductCard } from '../../api/marketplace';
import { pricedFrom, selectedEdition } from '../../lib/editions';

const STORAGE_KEY = 'auro-cart-v2';

export interface CartLine {
  productId: string;
  editionId?: string;
  slug: string;
  title: string;
  coverUrl: string | null;
  priceStars: number;
  priceRub: number | null;
  quantity: number;
  maxPerOrder: number;
  hasTribute?: boolean;
}

interface CartContextValue {
  lines: CartLine[];
  count: number;
  totalStars: number;
  totalRub: number;
  add: (product: ProductCard, quantity?: number, editionId?: string | null) => void;
  setQuantity: (productId: string, quantity: number, editionId?: string | null) => void;
  remove: (productId: string, editionId?: string | null) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function lineKey(productId: string, editionId?: string | null): string {
  return editionId ? `${productId}:${editionId}` : productId;
}

function readStored(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartLine[];
    return Array.isArray(parsed) ? parsed.filter((line) => line.productId && line.quantity > 0) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(readStored);

  const persist = (next: CartLine[]): void => {
    setLines(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const value = useMemo<CartContextValue>(() => {
    return {
      lines,
      count: lines.reduce((sum, line) => sum + line.quantity, 0),
      totalStars: lines.reduce((sum, line) => sum + line.priceStars * line.quantity, 0),
      totalRub: lines.reduce((sum, line) => sum + (line.priceRub ?? 0) * line.quantity, 0),
      add: (product, quantity = 1, editionId) => {
        persist(
          (() => {
            const edition = selectedEdition(product, editionId);
            const prices = pricedFrom(product, edition);
            const key = lineKey(product.id, edition?.id);
            const current = lines.find((line) => lineKey(line.productId, line.editionId) === key);
            const nextQty = Math.min(product.maxPerOrder, (current?.quantity ?? 0) + quantity);
            const title = edition ? `${product.title} · ${edition.title}` : product.title;

            if (current) {
              return lines.map((line) =>
                lineKey(line.productId, line.editionId) === key
                  ? { ...line, quantity: nextQty, priceStars: prices.priceStars, priceRub: prices.priceRub, title }
                  : line,
              );
            }

            return [
              ...lines,
              {
                productId: product.id,
                editionId: edition?.id,
                slug: product.slug,
                title,
                coverUrl: product.coverUrl,
                priceStars: prices.priceStars,
                priceRub: prices.priceRub,
                quantity: nextQty,
                maxPerOrder: product.maxPerOrder,
                hasTribute: Boolean(product.hasTribute),
              },
            ];
          })(),
        );
      },
      setQuantity: (productId, quantity, editionId) => {
        const key = lineKey(productId, editionId);

        if (quantity <= 0) {
          persist(lines.filter((line) => lineKey(line.productId, line.editionId) !== key));
          return;
        }

        persist(
          lines.map((line) =>
            lineKey(line.productId, line.editionId) === key
              ? { ...line, quantity: Math.min(line.maxPerOrder, quantity) }
              : line,
          ),
        );
      },
      remove: (productId, editionId) =>
        persist(lines.filter((line) => lineKey(line.productId, line.editionId) !== lineKey(productId, editionId))),
      clear: () => persist([]),
    };
  }, [lines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart вне CartProvider');
  return value;
}
